import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Brain, BookOpen, PenTool, Sparkles, ArrowLeft, ArrowRight,
  BookText, Wand2, MessageCircle, Target, Lightbulb,
  GraduationCap, CheckCircle2, Star
} from 'lucide-react';

const AIHub = () => {
  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState(null);

  const aiTools = [
    {
      id: 'reading',
      title: 'AI Reading Lab',
      subtitle: 'Smart reading with AI assistance',
      description: (
        <>
          Read passages with <strong>AI-powered comprehension tools</strong>, <strong>vocabulary building</strong>, and <strong>instant explanations</strong>.
        </>
      ),
      icon: BookOpen,
      iconBg: 'bg-violet-600',
      cardBg: 'bg-violet-50',
      borderColor: 'border-violet-200',
      hoverBorder: 'hover:border-violet-400',
      titleColor: 'text-violet-900',
      subtitleColor: 'text-violet-600',
      buttonBg: 'bg-violet-600 hover:bg-violet-700',
      features: [
        { icon: Wand2, text: 'AI Passage Generator' },
        { icon: BookText, text: 'Smart Reading Mode' },
        { icon: Lightbulb, text: 'Vocabulary Builder' },
        { icon: Target, text: 'Comprehension Questions' }
      ],
      stats: { label: '500+ passages', sublabel: 'across genres' },
      route: '/elibrary',
      testId: 'ai-reading-lab-card'
    },
    {
      id: 'writing',
      title: 'AI Writing Coach',
      subtitle: 'Creative writing with instant feedback',
      description: (
        <>
          Practice creative writing with <strong>50+ prompts</strong>, get <strong>instant AI analysis</strong>, and <strong>chat with Ed</strong> for guidance.
        </>
      ),
      icon: PenTool,
      iconBg: 'bg-amber-600',
      cardBg: 'bg-amber-50',
      borderColor: 'border-amber-200',
      hoverBorder: 'hover:border-amber-400',
      titleColor: 'text-amber-900',
      subtitleColor: 'text-amber-600',
      buttonBg: 'bg-amber-600 hover:bg-amber-700',
      features: [
        { icon: Sparkles, text: '50+ Writing Prompts' },
        { icon: CheckCircle2, text: 'Instant AI Analysis' },
        { icon: MessageCircle, text: 'Chat with Ed Tutor' },
        { icon: Star, text: 'Track Your Progress' }
      ],
      stats: { label: '8 categories', sublabel: 'of writing styles' },
      route: '/writing-coach',
      testId: 'ai-writing-coach-card'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
            
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-gray-900 text-lg">AI Learning Hub</span>
            </div>
            
            <div className="w-24" />
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-10">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <Badge className="bg-violet-100 text-violet-700 border-violet-200 mb-4">
            <Sparkles className="w-3 h-3 mr-1" />
            Powered by AI
          </Badge>
          
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Your AI Learning{' '}
            <span className="bg-gradient-to-r from-violet-600 to-amber-500 bg-clip-text text-transparent">
              Companions
            </span>
          </h1>
        </motion.div>

        {/* Meet Ed Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-10"
        >
          <Card className="bg-white border-gray-200 shadow-sm overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-violet-500 to-amber-500" />
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center gap-6">
                {/* Ed Avatar */}
                <div className="relative shrink-0">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg">
                    <Brain className="w-12 h-12 text-white" />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-green-500 border-4 border-white flex items-center justify-center shadow">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                </div>

                {/* Ed Info */}
                <div className="text-center md:text-left flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Meet Ed, Your AI Tutor
                  </h3>
                  <p className="text-gray-600 mb-4 max-w-xl leading-relaxed">
                    Ed is available in both the Reading Lab and Writing Coach. Ask questions, 
                    get explanations, request feedback, or just chat about what you're learning. 
                    Ed adapts to your level and learning style.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                    <Badge className="bg-violet-100 text-violet-700 border-violet-200">
                      <MessageCircle className="w-3 h-3 mr-1" />
                      Always Available
                    </Badge>
                    <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                      <Target className="w-3 h-3 mr-1" />
                      Personalized Help
                    </Badge>
                    <Badge className="bg-green-100 text-green-700 border-green-200">
                      <GraduationCap className="w-3 h-3 mr-1" />
                      UK Curriculum Aligned
                    </Badge>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="flex flex-col gap-2 shrink-0">
                  <Button
                    onClick={() => navigate('/elibrary')}
                    className="bg-violet-600 hover:bg-violet-700 text-white shadow"
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    Start Reading
                  </Button>
                  <Button
                    onClick={() => navigate('/writing-coach')}
                    variant="outline"
                    className="border-amber-500 text-amber-600 hover:bg-amber-50"
                  >
                    <PenTool className="w-4 h-4 mr-2" />
                    Start Writing
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* AI Tools Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {aiTools.map((tool, index) => {
            const Icon = tool.icon;
            const isHovered = hoveredCard === tool.id;
            
            return (
              <motion.div
                key={tool.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                onMouseEnter={() => setHoveredCard(tool.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <Card 
                  className={`relative overflow-hidden cursor-pointer transition-all duration-300 ${tool.cardBg} ${tool.borderColor} border-2 ${tool.hoverBorder} hover:shadow-lg ${
                    isHovered ? 'scale-[1.02]' : ''
                  }`}
                  onClick={() => navigate(tool.route)}
                  data-testid={tool.testId}
                >
                  <CardContent className="p-6">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`p-3 rounded-xl ${tool.iconBg} shadow-lg`}>
                          <Icon className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h3 className={`text-xl font-bold ${tool.titleColor}`}>{tool.title}</h3>
                          <p className={`text-sm ${tool.subtitleColor}`}>{tool.subtitle}</p>
                        </div>
                      </div>
                      <Badge className="bg-white text-gray-600 border-gray-200 shadow-sm">
                        <Brain className="w-3 h-3 mr-1" />
                        AI
                      </Badge>
                    </div>

                    {/* Description */}
                    <p className="text-gray-700 text-sm mb-5 leading-relaxed">
                      {tool.description}
                    </p>

                    {/* Features Grid */}
                    <div className="grid grid-cols-2 gap-3 mb-5">
                      {tool.features.map((feature, idx) => {
                        const FeatureIcon = feature.icon;
                        return (
                          <div 
                            key={idx}
                            className="flex items-center gap-2 text-sm text-gray-600"
                          >
                            <FeatureIcon className="w-4 h-4 text-gray-500" />
                            <span>{feature.text}</span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Stats & CTA */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <div>
                        <p className={`font-semibold ${tool.titleColor}`}>{tool.stats.label}</p>
                        <p className="text-gray-500 text-xs">{tool.stats.sublabel}</p>
                      </div>
                      <Button 
                        className={`${tool.buttonBg} text-white shadow`}
                      >
                        Explore
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default AIHub;
